// Live pricing for the Build-An-Order screen.
//
// W36 (2026-10-20): the screen quotes every price through
// POST /v1/pricing/calculate (services/api-server/pricing/compose-resolver.mjs),
// the tier -> SKU -> subscription -> bundle chain over Shopify. The
// request and response shapes below are the server's contract; the
// screen renders the engine's `unitPrice` / `subtotal` / `discounts`
// and never a typed price (feedback_pricing_engine_authority).
//
// Transport: a RELATIVE fetch with the session headers the W24 verifier
// bypass needs (Authorization + X-Customer-Id + X-Portal-Id, plus
// X-User-Id / X-User-Email on url-bootstrap sessions), the same contract
// useContactProfile / useContactAddresses use. The runtime-client path
// (useScreenRuntime) is null in production because the workspace
// container ships without VITE_RUNTIME_API_BASE_URL (nginx proxies /v1),
// so the engine client takes a header provider instead of a client.
// Never a bare `fetch` reference as a method (W38 "Illegal invocation").
//
// Tax, shipping and the final total stay "pending" until a Shopify
// draft-order calculate is wired behind LIVE_WRITE_GATE_SHOPIFY_CREATE_DRAFT_ORDER
// (OFF); the design explicitly supports "final total pending".
//
// The R5 helpers at the bottom (createLivePreviewQuoteLoader,
// recordConsent) are the runtime-client path liveServices.ts still
// exports; they are unchanged.

import type { RuntimeApiClient } from '../api/runtimeClient.js';
import type { PreviewQuote, PreviewQuoteLoader, PreviewQuoteRequest } from
  '../screens/BuildOrderReview/orderState.js';

export const PRICING_CALCULATE_PATH = '/v1/pricing/calculate';
export const PRICING_NOT_CONFIGURED_CODE = 'PRICING_RESOLVER_NOT_CONFIGURED';

export type PurchaseType = 'one_time' | 'subscription';
export type QuotePurpose = 'basket' | 'catalog';
export type PricingTier = 'standard' | 'barn' | 'dealer' | 'vet' | 'nonprofit';

export interface EngineLineRequest {
  productHandle: string;
  units: number;
  hasActiveSub?: boolean;
}

export interface EngineQuoteRequest {
  hubspotContactId: string;
  workId?: string | null;
  purchaseType: PurchaseType;
  cadenceDays?: string | null;
  purpose?: QuotePurpose;
  lineItems: EngineLineRequest[];
}

export interface EngineWarning {
  code: string;
  detail?: Record<string, unknown>;
}

export interface EngineDiscount {
  source: string;
  label?: string;
  pct: number | null;
  amount: number | null;
  sku?: string;
  units?: number;
}

export interface EngineLine {
  productHandle: string;
  sku: string | null;
  variantId: string | null;
  title: string;
  units: number;
  /** One-time, tier-aware unit price in cents; null when Shopify had no SKU. */
  basePrice: number | null;
  /** Unit price at this purchase type in cents (20% off on a standard subscription). */
  unitPrice: number | null;
  lineTotal: number | null;
  priceSource: string | null;
  discounts: EngineDiscount[];
  available: boolean | null;
  warnings: EngineWarning[];
}

export interface EngineBundleRow {
  sku: string;
  units: number;
  items: Record<string, number>;
  oneTimePriceCents: number;
  lineOneTimeCents: number;
  lineSubscriberCents: number | null;
  savingsCents: number;
  singlesCents: number;
}

export interface EngineBundleOutcome {
  matchedRows: EngineBundleRow[];
  bundleOneTime: number;
  bundleSubscriber: number | null;
  remainderLineItems: Array<{ productHandle: string; units: number }>;
  savingsVsSingles: number;
  singlesBaselineCents: number;
  bestOneTime: number;
  subDiscountApplied: boolean;
}

export interface EngineContact {
  hubspotContactId: string;
  read: 'ok' | 'unavailable' | 'not_found';
  shopifyCustomerGid: string | null;
  shopifyTags: string[] | null;
  tagsSource: string | null;
  tierMatchedValue: string | null;
  customerTier: string | null;
  tierSourceProperty: string | null;
  subscriptionStatus: string | null;
  subscriptionProductSku: string | null;
}

export interface EngineQuote {
  status: 'ok';
  hubspotContactId: string;
  workId: string | null;
  purchaseType: PurchaseType;
  cadenceDays: string | null;
  purpose: QuotePurpose;
  tier: PricingTier;
  tierSource: 'hubspot' | 'shopify' | 'default';
  lineItems: EngineLine[];
  bundleOutcome: EngineBundleOutcome | null;
  /** Products total at this purchase type, cents; null when any line is unpriced. */
  subtotal: number | null;
  oneTimeSubtotal: number | null;
  discounts: EngineDiscount[];
  shipping: null;
  tax: 'pending';
  total: 'pending';
  currency: string;
  quoteRevision: string;
  catalog: Array<{ productHandle: string; title: string }>;
  contact: EngineContact;
  warnings: EngineWarning[];
  source: string;
  computedAt: number;
}

export type EngineQuoteResult =
  | { kind: 'ok'; quote: EngineQuote }
  /** 503 PRICING_RESOLVER_NOT_CONFIGURED: the token is not bound; prices stay pending. */
  | { kind: 'pending'; status: number; errorCode: string }
  | { kind: 'error'; status: number; errorCode: string; message?: string };

export type HeaderProvider = () => Record<string, string> | null;

export interface EnginePricingClientDeps {
  headers: HeaderProvider;
  fetchImpl?: typeof fetch;
  path?: string;
}

export interface EnginePricingClient {
  quote(request: EngineQuoteRequest): Promise<EngineQuoteResult>;
}

function readErrorCode(body: unknown, status: number): string {
  if (body && typeof body === 'object' && typeof (body as { errorCode?: unknown }).errorCode === 'string') {
    return (body as { errorCode: string }).errorCode;
  }
  return `HTTP_${status}`;
}

export function createEnginePricingClient({ headers, fetchImpl, path = PRICING_CALCULATE_PATH }: EnginePricingClientDeps): EnginePricingClient {
  // Resolve the global at call time (never `fetch` captured as a method).
  const doFetch: typeof fetch = fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  return {
    async quote(request) {
      const sessionHeaders = headers();
      if (!sessionHeaders) return { kind: 'error', status: 0, errorCode: 'SESSION_UNAVAILABLE' };
      let response: Response;
      try {
        response = await doFetch(path, {
          method: 'POST',
          headers: { ...sessionHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
        });
      } catch (err) {
        return { kind: 'error', status: 0, errorCode: 'FETCH_FAILED', message: err instanceof Error ? err.message : undefined };
      }
      let body: unknown = null;
      try {
        body = await response.json();
      } catch {
        body = null;
      }
      if (!response.ok) {
        const errorCode = readErrorCode(body, response.status);
        if (response.status === 503 && errorCode === PRICING_NOT_CONFIGURED_CODE) {
          return { kind: 'pending', status: response.status, errorCode };
        }
        return { kind: 'error', status: response.status, errorCode };
      }
      if (!body || typeof body !== 'object' || (body as { status?: unknown }).status !== 'ok' || !Array.isArray((body as { lineItems?: unknown }).lineItems)) {
        return { kind: 'error', status: response.status, errorCode: 'QUOTE_SHAPE_INVALID' };
      }
      return { kind: 'ok', quote: body as EngineQuote };
    },
  };
}

export function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export const TIER_LABELS: Record<PricingTier, string> = {
  standard: 'Standard pricing',
  barn: 'Barn pricing',
  vet: 'Vet pricing',
  nonprofit: 'Non-profit pricing',
  dealer: 'Dealer pricing (QuickBooks)',
};

export function tierLabel(tier: PricingTier | string): string {
  return TIER_LABELS[tier as PricingTier] ?? `${tier} pricing`;
}

export function tierSourceLabel(quote: Pick<EngineQuote, 'tierSource' | 'contact'>): string {
  if (quote.tierSource === 'hubspot') return `HubSpot ${quote.contact.tierMatchedValue ?? 'pricing group'}`;
  if (quote.tierSource === 'shopify') {
    const via = quote.contact.tagsSource === 'hubspot_shopify_tags' ? 'HubSpot copy of the Shopify tags' : 'Shopify customer tag';
    return `${via} "${quote.contact.tierMatchedValue ?? ''}"`;
  }
  return 'no pricing tag on the customer';
}

/** The line-level discount entry the engine emits for a standard subscription. */
export const SUBSCRIPTION_DISCOUNT_SOURCE = 'subscription_20pct';
export const BUNDLE_DISCOUNT_PREFIX = 'bundle:';

// --- R5 runtime-client path (unchanged) ---------------------------------

export interface LivePricingDeps {
  runtimeClient: RuntimeApiClient;
}

/**
 * Build a PreviewQuoteLoader backed by the R5 pricing endpoint through a
 * RuntimeApiClient (the legacy {customerId, lineItems[{sku, quantity}]}
 * body the injected resolveOrderTotals resolver serves). Kept for
 * liveServices.ts; the screen itself now uses createEnginePricingClient.
 */
export function createLivePreviewQuoteLoader({ runtimeClient }: LivePricingDeps): PreviewQuoteLoader {
  return async function loadPreviewQuote(request: PreviewQuoteRequest): Promise<PreviewQuote> {
    const body = {
      customerId: request.customerId,
      workId: request.workId,
      lineItems: request.basket.map((line) => ({
        sku: line.sku,
        quantity: line.quantity,
      })),
      shippingAddress: {
        address1: request.address.street,
        address2: request.address.line2 ?? '',
        city: request.address.city,
        province: request.address.region,
        zip: request.address.postal,
        country: request.address.country,
      },
      currency: 'USD',
      includeShipping: true,
    };
    const response = await (runtimeClient as unknown as {
      request<T>(opts: { method: 'POST'; path: string; body: unknown }): Promise<T>;
    }).request<{
      status: string;
      currency?: string;
      subtotal?: number;
      tax?: number;
      shipping?: number;
      discounts?: number;
      total?: number;
      invalidatedConsents?: number;
      reason?: string;
    }>({
      method: 'POST',
      path: PRICING_CALCULATE_PATH,
      body,
    });
    if (response.status !== 'ok') {
      // Signal failure by returning a non-matching revision so validPreviewQuote
      // returns false and the UI surfaces "could not match quote". The screen
      // already blocks the Confirm button in this state.
      const subtotal = request.basket.reduce((sum, l) => sum + l.quantity * l.exampleUnitCents, 0);
      return {
        kind: 'design-preview',
        contextKey: '__UNAVAILABLE__',
        revision: 'unavailable',
        taxCents: 0,
        totalCents: subtotal,
      };
    }
    return {
      kind: 'design-preview',
      contextKey: request.contextKey,
      revision: String(Date.now()),
      taxCents: Number(response.tax ?? 0),
      totalCents: Number(response.total ?? 0),
    };
  };
}

/**
 * Record customer consent against the server. Returns the fingerprint
 * the server computed so the caller can carry it on the charge call.
 */
export async function recordConsent({
  runtimeClient,
  customerId,
  workId,
  orderId = null,
  subscriptionId = null,
  consentType,
  totals,
  termsVersion,
}: {
  runtimeClient: RuntimeApiClient;
  customerId: string;
  workId: string;
  orderId?: string | null;
  subscriptionId?: string | null;
  consentType: 'one_time_charge' | 'recurring_agreement';
  totals: { currency: string; subtotal: number; discounts: number; shipping: number; tax: number; total: number };
  termsVersion: string;
}): Promise<{ ok: boolean; eventId?: string; fingerprint?: string; error?: string }> {
  try {
    const response = await (runtimeClient as unknown as {
      request<T>(opts: { method: 'POST'; path: string; body: unknown }): Promise<T>;
    }).request<{
      ok: boolean;
      event?: { id: string; totalsFingerprint: string };
    }>({
      method: 'POST',
      path: '/v1/pricing/consent',
      body: { customerId, workId, orderId, subscriptionId, consentType, totals, termsVersion },
    });
    if (!response.ok || !response.event) return { ok: false, error: 'CONSENT_REJECTED' };
    return { ok: true, eventId: response.event.id, fingerprint: response.event.totalsFingerprint };
  } catch (err) {
    return { ok: false, error: (err as Error)?.message || 'CONSENT_FAILED' };
  }
}
